<script setup lang="ts">
import { computed, ref } from 'vue'
import { degreeService } from '@/services/degreeService'
import { useStore } from 'vuex'
import { IExtendedDegree } from '@shared/types/extensions'
import BaseModal from '../BaseModal.vue'
const props = defineProps<{
	modelValue: boolean
	institutionId: string
	selectedDegrees: IExtendedDegree[]
	isTestMode: boolean
}>()
const store = useStore()
const emit = defineEmits<{
	'update:modelValue': [value: boolean]
	'update:packageView': []
}>()
const showModal = computed({
	get: () => props.modelValue,
	set: (value: boolean) => emit('update:modelValue', value)
})

const packageName = ref('')
const hasErrors = ref(false)
const hasWarnings = ref(false)
const selectedDegreesCount = computed(() => props.selectedDegrees.length)
const showErrorModal = ref(false)
const errorMessage = ref<string>('')

const createPackage = async () => {
	if (!packageName.value) {
		showErrorModal.value = true
		errorMessage.value = 'Debe ingresar un nombre para el paquete'
		return
	}

	try {
		const selectedDegrees = props.selectedDegrees.filter(d => d.selected)
		const degreesMap = selectedDegrees.reduce(
			(acc, degree) => {
				acc[degree.id] = true
				return acc
			},
			{} as Record<string, boolean>
		)
		hasErrors.value = props.selectedDegrees.some(degree => degree.error)
		hasWarnings.value = props.selectedDegrees.some(degree => degree.warning)
		if (!hasErrors.value && !hasWarnings.value) {
			await degreeService.createPackage(degreesMap, props.institutionId, packageName.value, props.isTestMode)
		} else {
			store.dispatch('notification/showError', 'No se puede crear un paquete con títulos que contengan errores o advertencias')
			return
		}

		store.dispatch('notification/showSuccess', 'Paquete creado exitosamente')
		showModal.value = false
		packageName.value = ''
		emit('update:packageView')
	} catch (error) {
		handleError(error)
	}
}
const handleError = (error: any) => {
	if (error.message.includes('Unauthorized access to institution')) {
		store.dispatch('notification/showError', 'No tienes acceso a esta institución')
	} else {
		store.dispatch('notification/showError', error.message)
	}
}
const getResponsables = (degree: IExtendedDegree) => {
	const responsables: string[] = []
	for (const responsable of Object.keys(degree.data?.FirmaResponsables)) {
		responsables.push(responsable)
	}
	return responsables.join(', ')
}
</script>

<template>
	<VaModal v-model="showModal" hide-default-actions noDismiss noEscDismiss noOutsideDismiss background-color="transparent">
		<VaCard>
			<VaCardTitle :class="props.isTestMode ? 'bg-danger' : 'bg-primary'">
				<div class="row align-items-center" style="color: white">
					<div class="col-md-11 text-center">
						<div class="row">
							<div class="col-md-12 text-center" style="font-size: 22px; font-weight: bold">Crear nuevo paquete</div>
						</div>
					</div>
				</div>
			</VaCardTitle>
			<VaCardContent class="p-4">
				<div class="certificates-scroll">
					<VaInput v-model="packageName" label="Nombre del Paquete" class="mb-2" style="width: 100%" />
					<p class="font-medium mb-2">Elementos seleccionados ({{ selectedDegreesCount }}):</p>
					<VaCard v-for="degree in selectedDegrees" :key="degree.id" class="mb-3" :color="'primary'" gradient hoverable>
						<VaCardContent>
							<div class="flex items-start justify-between">
								<div class="flex-1">
									<div class="font-semibold text-lg text-white mb-1">
										<VaIcon name="school" class="text-white/80" style="margin-right: 10px" />
										<span class="text-lg text-white/90 font-bold">
											{{ degree.data?.Profesionista?.nombre }}
											{{ degree.data?.Profesionista?.primerApellido }}
											{{ degree.data?.Profesionista?.segundoApellido }}
										</span>
										<div class="w-full text-right">
											<span class="text-sm text-white/90 font-bold">CURP: {{ degree.data.Profesionista.curp }}</span>
										</div>
									</div>
									<div class="flex items-center gap-2 mb-2">
										<span class="text-sm text-white/90">{{ degree.data?.Carrera?.nombreCarrera }}</span>
									</div>
									<div class="flex items-center mb-2">
										<span class="text-sm text-white/90 ml-2">Responsables a firmar: {{ getResponsables(degree) }}</span>
									</div>
								</div>
							</div>
						</VaCardContent>
					</VaCard>
				</div>
			</VaCardContent>
			<VaCardActions class="d-flex justify-content-end">
				<VaButton color="danger" @click="showModal = false" class="btn-cancel" style="padding: 8px 16px; font-weight: 500; letter-spacing: 0.5px" size="small">
					<VaIcon name="close" style="margin-right: 4px" />
					Cancelar
				</VaButton>
				<VaButton color="primary" @click="createPackage" class="btn-create" style="margin-left: 12px; padding: 8px 20px; font-weight: 600; letter-spacing: 0.5px" size="small">
					<VaIcon name="done" style="margin-right: 4px" />
					Crear Paquete
				</VaButton>
			</VaCardActions>
		</VaCard>
	</VaModal>
	<BaseModal v-model="showErrorModal" title="Error" :message="errorMessage" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ errorMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>
<style scoped>
.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
.certificates-scroll {
	max-height: 70vh;
	overflow-y: auto;
	padding-right: 8px;
}
</style>
